import styled from '@emotion/styled';
import React, { useState, useEffect } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import useUserContext from '../../../hooks/use-user-context';
import { createOptionMap, isBrowser } from '../../../utilities/misc';
import applyJSONTemplates from '../../../utilities/json-templates';

import { containerStyles } from '../../../styles/layout';
import { fontWeights } from '../../../theme/type';
import { colors } from '../../../theme/colors';
import { rem } from '../../../utilities/style';
import { media } from '../../../theme/media';
import { shadow } from '../../../styles/box';
import { detectOS, detectPlatform, detectBrowser } from '../../../utilities/detectOS';

import { ReactComponent as IconRemoteFrom } from '../../../images/icons/downloads-remote-from.svg';
import { ReactComponent as IconRemoteTo } from '../../../images/icons/downloads-remote-to.svg';
import DownloadTab from './DownloadTab';
import Tooltip from '../../Tooltip';
import TextRenderer from '../../TextRenderer';

const Container = styled.div`
  padding: ${rem(32)} 0;
  background-color: ${({ backgroundColor }) => (backgroundColor === 'Blue' ? colors.blue['50'] : colors.white)};

  ${media('>tiny')} {
    padding: ${rem(48)} 0;
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.small}
`;

const Card = styled.div`
  ${shadow}
  padding: ${rem(16)};
  border-radius: ${rem(10)};
  background: ${colors.white};

  ${media('>tiny')} {
    padding: ${rem(24)};
  }

  ${media('>small')} {
    padding: ${rem(40)};
  }
`;

const CardHeader = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.gray[200]};
`;

const TabOption = styled.div`
  flex: 1 1 50%;
  position: relative;
  z-index: 1;
  text-align: center;

  svg {
    display: block;
    margin: 0 auto;
  }

  ${Tooltip} {

    ${media('<=tiny')} {
      display: block;
      margin: ${rem(-24)} auto ${rem(8)} auto;
      p {
        max-width: 50vw;
      }
    }
  }
`;

const TabSwitch = styled.button`
  margin-top: ${rem(4)};
  margin-right: ${rem(4)};
  margin-bottom: ${rem(24)};
  padding: 0;
  border: 0;
  font-weight: ${fontWeights.semiBold};
  font-size: ${rem(14)};
  line-height: 1.2;
  background: 0;
  color: ${colors.blue['800']};
  cursor: pointer;

  ${media('>tiny')} {
    font-size: ${rem(16)};
  }

  &::before {
    content: '';
    position: absolute;
    z-index: 2;
    inset: 0;
    background: ${colors.white};
    opacity: 0.5;
    transition: opacity 0.2s ease-in-out;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    height: ${rem(3)};
    background: ${colors.green['500']};
    transform: scaleX(0);
    transition: transform 0.2s ease;
  }

  &[aria-selected="true"] {

    &::before {
      z-index: auto;
      opacity: 0;
    }

    &::after {
      transform: none;
    }
  }
`;

const Tabs = styled.div`
`;

const DownloadTabs = ({ backgroundColor, product }) => {
  const data = useStaticQuery(graphql`
    query DownloadTabs {
      allContentfulOptionSet(filter: {name: {eq: "downloads"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);

  const { language, country } = useUserContext();
  const [activeTab, setActiveTab] = useState('remoteFrom');
  const [currentOs, setCurrentOs] = useState(null);
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  // const downloadSections = Object.keys(options['downloads.download_tabs']);
  const products = applyJSONTemplates(options['downloads.download_tabs']);
  // console.log('⭐', product);
  const [remoteFrom, remoteTo] = products[product] || products.sos;
  const changeTab = (tabName) => {
    setActiveTab(tabName);
  };
  // useEffect(() => {
  //   debug.log('‍🚒 Active tab:', activeTab);
  // }, [activeTab]);
  const currentTab = activeTab;

  // Check's the user's OS
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const autoDownload = urlParams.get('autodownload');
    if (isBrowser) {
      const { userAgent } = window.navigator;
      // auto opening download section
      const osDetected = detectOS(userAgent);
      // auto downloading business app
      const platformDetected = detectPlatform(userAgent);
      // detects the browser
      const browserDetected = detectBrowser(userAgent);
      setCurrentOs(osDetected);

      // Autodownload link if URL has ?autodownload=1
      if (autoDownload === '1') {
        const downloadLinks = {
          windows: 'https://redirect.splashtop.com/my/src/win',
          mac: 'https://redirect.splashtop.com/my/src/mac',
          linux: 'https://support-splashtopbusiness.splashtop.com/hc/articles/4404715685147',
          chromebook: country === 'cn'
            ? 'https://download-cn.splashtop.com/droidclient/stb/splashtop_business_arm.apk'
            : 'https://play.google.com/store/apps/details?id=com.splashtop.remote.business',
          iphone: 'https://apps.apple.com/app/splashtop-business/id650739354',
          ipad: 'https://apps.apple.com/app/splashtop-business/id650739354',
          android: country === 'cn'
            ? 'https://download-cn.splashtop.com/droidclient/stb/splashtop_business_arm.apk'
            : 'https://play.google.com/store/apps/details?id=com.splashtop.remote.business',
          amazon: 'https://www.amazon.com/Splashtop-Inc-Business/dp/B011168LIE/',
        };

        const downloadUrl = downloadLinks[platformDetected];

        if (downloadUrl) {
          if ((downloadUrl.includes('play.google.com')
          || downloadUrl.includes('support-splashtopbusiness.splashtop.com')
          || downloadUrl.includes('amazon.com')
          || downloadUrl.includes('apple.com'))
          && (browserDetected !== 'Firefox')) {
            window.location.href = downloadUrl;
          } else {
            // Create an iframe for direct download links
            const iframe = document.createElement('iframe');
            iframe.style.height = '0';
            iframe.style.overflow = 'hidden';
            iframe.src = downloadUrl;
            document.body.appendChild(iframe); // Append iframe to the body (hidden from view)
          }
        }
      }
    }
  }, [country]);

  return (
    <Container
      backgroundColor={backgroundColor}
    >
      <ContentWrapper>
        <Card>
          {remoteFrom && remoteTo && (
            <CardHeader>
              <TabOption onClick={() => changeTab('remoteFrom')}>
                <IconRemoteFrom aria-hidden="true" />
                <TabSwitch
                  id="remote-from-toggle"
                  aria-controls="remote-from-panel"
                  aria-selected={activeTab === 'remoteFrom' ? 'true' : 'false'}
                >
                  <TextRenderer text={options['downloads.remote_from_tab_label']} />
                </TabSwitch>
                <Tooltip>
                  <TextRenderer text={options['downloads.remote_from_tab_tooltip']} />
                </Tooltip>
              </TabOption>
              <TabOption onClick={() => changeTab('remoteTo')}>
                <IconRemoteTo aria-hidden="true" />
                <TabSwitch
                  id="remote-to-toggle"
                  aria-controls="remote-to-panel"
                  aria-selected={activeTab === 'remoteTo' ? 'true' : 'false'}
                >
                  <TextRenderer text={options['downloads.remote_to_tab_label']} />
                </TabSwitch>
                <Tooltip>
                  <TextRenderer text={options['downloads.remote_to_tab_tooltip']} />
                </Tooltip>
              </TabOption>
            </CardHeader>
          )}
          <Tabs>
            {remoteFrom && (
              <DownloadTab
                id="remote-from-panel"
                role="tabpanel"
                aria-labelledby="remote-from-toggle"
                activeTab={activeTab === 'remoteFrom'}
                currentOs={currentOs}
                setCurrentOs={setCurrentOs}
                data={remoteFrom.platforms}
                productName={product}
                currentTab={currentTab}
              />
            )}
            {remoteTo && (
              <DownloadTab
                id="remote-to-panel"
                role="tabpanel"
                aria-labelledby="remote-to-toggle"
                activeTab={activeTab === 'remoteTo'}
                currentOs={currentOs}
                setCurrentOs={setCurrentOs}
                data={remoteTo.platforms}
                productName={product}
                currentTab={currentTab}
              />
            )}
          </Tabs>
        </Card>
      </ContentWrapper>
    </Container>
  );
};

export const query = graphql`
  fragment DownloadTabsFragment on ContentfulDownloadTabs {
    id
    __typename
    product
    backgroundColor
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default DownloadTabs;
